import React from 'react';
import Loading from './Loading';

const Button = ({
  children,
  variant = 'primary',
  size = 'md',
  loading = false,
  disabled = false,
  fullWidth = false,
  leftIcon,
  rightIcon,
  className = '',
  ...props
}) => {
  const baseClasses = 'inline-flex items-center justify-center font-medium rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 transition-all duration-300 transform hover:scale-105 active:scale-95';

  // Updated color schemes to match villa detail page
  const variants = {
    // Primary - Blue theme (matching villa page blue-600)
    primary: 'bg-blue-600 text-white hover:bg-blue-700 focus:ring-blue-500 disabled:bg-blue-300 shadow-md hover:shadow-lg',
    
    // Secondary - Gray theme
    secondary: 'bg-gray-600 text-white hover:bg-gray-700 focus:ring-gray-500 disabled:bg-gray-300 shadow-md hover:shadow-lg',
    
    // Outline - Blue outline to match primary
    outline: 'border-2 border-blue-600 text-blue-600 bg-white hover:bg-blue-50 focus:ring-blue-500 disabled:border-gray-300 disabled:text-gray-300 shadow-sm hover:shadow-md',
    
    // Ghost - Subtle blue
    ghost: 'text-blue-600 bg-transparent hover:bg-blue-50 focus:ring-blue-500 disabled:text-gray-300 hover:shadow-sm',
    
    // Danger - Red theme
    danger: 'bg-red-600 text-white hover:bg-red-700 focus:ring-red-500 disabled:bg-red-300 shadow-md hover:shadow-lg',
    
    // Success - Green theme (matching WhatsApp button)
    success: 'bg-green-600 text-white hover:bg-green-700 focus:ring-green-500 disabled:bg-green-300 shadow-md hover:shadow-lg',
    
    // WhatsApp - Special variant for WhatsApp buttons
    whatsapp: 'bg-green-600 text-white hover:bg-green-700 focus:ring-green-500 disabled:bg-green-300 shadow-lg hover:shadow-xl',
    
    // Light - Light background with dark text
    light: 'bg-gray-100 text-gray-800 hover:bg-gray-200 focus:ring-gray-400 disabled:bg-gray-50 disabled:text-gray-400 shadow-sm hover:shadow-md',
    
    // Dark - Dark background
    dark: 'bg-gray-800 text-white hover:bg-gray-900 focus:ring-gray-600 disabled:bg-gray-400 shadow-md hover:shadow-lg',
    
    // Featured - Gold/Yellow for featured items
    featured: 'bg-yellow-500 text-white hover:bg-yellow-600 focus:ring-yellow-400 disabled:bg-yellow-300 shadow-md hover:shadow-lg',
    
    // Gradient - Blue gradient for special CTAs
    gradient: 'bg-gradient-to-r from-blue-600 to-blue-700 text-white hover:from-blue-700 hover:to-blue-800 focus:ring-blue-500 disabled:from-blue-300 disabled:to-blue-400 shadow-lg hover:shadow-xl'
  };

  // Enhanced sizes with better spacing
  const sizes = {
    xs: 'px-2 py-1 text-xs',
    sm: 'px-3 py-1.5 text-sm',
    md: 'px-4 py-2 text-sm',
    lg: 'px-6 py-3 text-base',
    xl: 'px-8 py-4 text-lg',
    xxl: 'px-10 py-5 text-xl' // For hero buttons
  };

  const classes = `
    ${baseClasses}
    ${variants[variant]}
    ${sizes[size]}
    ${fullWidth ? 'w-full' : ''}
    ${(disabled || loading) ? 'cursor-not-allowed opacity-60 transform-none hover:scale-100' : ''}
    ${className}
  `.trim();

  return (
    <button
      className={classes}
      disabled={disabled || loading}
      {...props}
    >
      {loading && <Loading size="sm" text="" />}
      {!loading && leftIcon && <span className="mr-2">{leftIcon}</span>}
      {!loading && children}
      {!loading && rightIcon && <span className="ml-2">{rightIcon}</span>}
    </button>
  );
};

export default Button;